import { useContext, useEffect, useRef, useState } from 'react';
import { CatalogDispatchContext } from '../store/CatalogContext';
import DeleteIcon from '../assets/delete-icon.svg?react';
import { useCartActions } from '../hooks/useCartActions';

import type { ProductState } from '../types/product';
import type { CartProduct } from '../types/cart';
import { formatCurrency } from '../utils/formatCurrency';
import { getDisplayPrice, shouldIncludeFees } from '../utils/amountHelper';
import { Link } from 'react-router';
// import { getOrigPrice } from '../utils/amountHelper';

interface CartWidgetItemsProps {
  product: CartProduct;
}

export default function CartWidgetItems({ product }: CartWidgetItemsProps) {
  const catalogDispatch = useContext(CatalogDispatchContext);

  const qtyDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // console.log('CartWidgetItem', product);

  const showFees = shouldIncludeFees(product.productId) && product.unitThirdPartyFee > 0;

  const [productState, setProductState] = useState<ProductState>({
    productId: product.productId,
    productInCart: product,
    quantity: product.quantity,
    upsells: product.upSellProducts || [],
    isUpdatingCart: false,
    isQuantityUpdated: false,
  });

  useEffect(() => {
    setProductState((prevState) => ({
      ...prevState,
      productInCart: product,
      quantity: product.quantity,
      upsells: product.upSellProducts,
    }));
  }, [product]);

  const { updateCart, removeProduct } = useCartActions({
    setState: setProductState,
    catalogDispatch,
  });

  const handleRemove = () => {
    removeProduct(productState);
  };

  // preventing non-number entry for the qty field
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    const allowedKeys = [
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      'Backspace',
      'Delete',
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Enter',
      'Control',
      'Meta',
      'a',
    ];
    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  };

  const handleQtyChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    // console.log(event);
    const eventType = event.type;
    const target = event.target as HTMLInputElement;
    const { value } = target;

    // Only allow empty string or a valid number
    const parsedValue = value === '' ? '' : Number(value);
    const quantity = eventType === 'blur' && parsedValue === '' ? product.quantity : parsedValue;

    setProductState((prevState) => ({
      ...prevState,
      quantity: quantity,
    }));

    // need to check if qty is a number before calling API
    if (quantity !== product.quantity && typeof quantity === 'number') {
      if (qtyDebounce.current) {
        clearTimeout(qtyDebounce.current);
      }
      qtyDebounce.current = setTimeout(() => {
        const product = {
          ...productState,
          quantity: quantity,
        };
        updateCart(product);
      }, 300);
    }

    // remove product if quantity is 0
    if (quantity === 0) {
      handleRemove();
    }
  };

  const handleQtyBtnClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    const target = event.target as HTMLButtonElement;
    const { name } = target;

    let quantity = typeof productState.quantity === 'number' ? productState.quantity : product.quantity;

    if (name === 'decrement' && quantity > 0) {
      quantity = quantity - 1;
    }
    if (name === 'increment') {
      quantity = quantity + 1;
    }

    setProductState((prevState) => ({
      ...prevState,
      quantity: quantity,
    }));

    if (qtyDebounce.current) {
      clearTimeout(qtyDebounce.current);
    }
    qtyDebounce.current = setTimeout(() => {
      const product = {
        ...productState,
        quantity: quantity,
      };
      updateCart(product);
    }, 300);
  };

  const handleBulkModal = () => {
    catalogDispatch({
      type: 'UX',
      payload: {
        showBulkRates: {
          name: product?.name || '',
          productBulkTiers: product?.cartProductBulkTiers || [],
        },
      },
    });
  };

  const toggleFeeDescription = () => {
    // show fee description
    catalogDispatch({
      type: 'UX',
      payload: {
        showFeeDescription: {
          name: product.thirdPartyFeeName || '',
          description:
            product.feeDescription ||
            'State, local or other regulatory agencies often charge fees that are not included in your course price. This processing or reporting fee is required for each learner.',
        },
      },
    });
  };

  const replaceUpsells = product.upSellProducts
    .filter((upsell) => upsell.upsellSectionId === 1 && upsell.isSelected)
    .sort((a, b) => a.upsellOrderIndex - b.upsellOrderIndex);

  const accessoryUpsells = product.upSellProducts
    .filter((upsell) => upsell.upsellSectionId === 2 && upsell.isSelected)
    .sort((a, b) => a.upsellOrderIndex - b.upsellOrderIndex);

  const digitalUpsells = product.upSellProducts
    .filter((upsell) => upsell.upsellSectionId === 3 && upsell.isSelected)
    .sort((a, b) => a.upsellOrderIndex - b.upsellOrderIndex);

  const shippingUpsells = product.upSellProducts
    .filter((upsell) => upsell.upsellSectionId === 4 && upsell.isSelected)
    .sort((a, b) => a.upsellOrderIndex - b.upsellOrderIndex);

  const relatedUpsells = product.upSellProducts
    .filter((upsell) => upsell.upsellSectionId === 5 && upsell.isSelected)
    .sort((a, b) => a.upsellOrderIndex - b.upsellOrderIndex);

  const selectedUpsells = [...replaceUpsells, ...accessoryUpsells, ...digitalUpsells, ...shippingUpsells, ...relatedUpsells];

  const itemUpsells = selectedUpsells.map((upsell) => {
    const itemDisplayPrice = getDisplayPrice(upsell, true) * product.quantity;
    return (
      <li className="list-group-item border-0 lh-14 p-0 mb-1" data-pid={upsell.productId} key={upsell.productId}>
        <div className="item-name">{upsell.name}</div>
        <div className="item-amount text-end">
          {upsell.upsellType === 'Replace' ? 'Included' : itemDisplayPrice === 0 ? 'Free' : formatCurrency(itemDisplayPrice)}
        </div>
      </li>
    );
  });

  return (
    <>
      <li
        className="list-group-item cart-item d-block px-0"
        data-pid={product.productId}
      >
        <div className="cart-item-detials d-flex fw-600 lh-14 py-1">
          <div className="item-name">
            <Link className="text-reset" to={`/product/?id=${product.productId}`}>
              {product.name}
            </Link>
          </div>
          <div className="item-amount text-end">
            {product && formatCurrency((product.listPrice - product?.combinedDiscount) * product.quantity)}
          </div>
        </div>

        {(itemUpsells || showFees) && (
          <ul className="cart-item-upsell list-group-flush bg-transparent ps-2 m-0 mt-1 mb-2">
            {showFees && (
              <li className="list-group-item border-0 lh-14 p-0 mb-1">
                <div className="item-name">
                  {product.thirdPartyFeeName}{' '}
                  <button
                    className="btn-toggle fee-description-toggle fw-400 small text-reset lh-1 p-0 ms-1"
                    type="button"
                    onClick={toggleFeeDescription}
                  >
                    <span className="info-icon align-middle ms-0">?</span>
                  </button>
                </div>
                <div className="item-amount text-end">{formatCurrency(product.unitThirdPartyFee * product.quantity)}</div>
              </li>
            )}

            {itemUpsells}
          </ul>
        )}

        <div className="cart-item-action d-flex align-items-center justify-content-between py-1">
          <div className="cart-item-qty d-flex align-items-center">
            <div className="input-group me-3 notranslate">
              {product.quantity === 1 ? (
                <button
                  className="qty-btn delete-btn btn btn-sm btn-light p-1 pt-0"
                  name="decrement"
                  type="button"
                  aria-label="Delete"
                  onClick={handleRemove}
                >
                  <DeleteIcon />
                </button>
              ) : (
                <button
                  className="qty-btn btn btn-sm btn-light"
                  name="decrement"
                  type="button"
                  aria-label="Decrement"
                  // disabled={productState.quantity === 1}
                  onClick={productState.quantity === 1 ? handleRemove : handleQtyBtnClick}
                >
                  &#8722;
                </button>
              )}

              <label className="visually-hidden" htmlFor={`qty-${product.productId}`}>
                Quantity
              </label>
              <input
                aria-label="Item Quantity"
                className="qty-input form-control"
                id={`qty-${product.productId}`}
                min="0"
                name="quantity"
                type="number"
                value={productState.quantity}
                onKeyDown={handleKeyDown}
                onChange={handleQtyChange}
                onBlur={handleQtyChange}
              />
              <button
                className="qty-btn btn btn-sm btn-light"
                name="increment"
                type="button"
                aria-label="Increment"
                onClick={handleQtyBtnClick}
              >
                &#43;
              </button>
            </div>

            {product?.cartProductBulkTiers && product?.cartProductBulkTiers.length > 0 && (
              <div className="bulk-discount-toggle d-inline-block text-muted lh-1">
                <button className="btn-toggle fw-400 small text-reset lh-1 p-0" type="button" onClick={handleBulkModal}>
                  Bulk Rates<span className="info-icon align-middle">?</span>
                </button>
              </div>
            )}
          </div>
          <div className="text-end text-muted">
            <button
              className="delete-btn btn text-muted"
              name="delete"
              type="button"
              aria-label="Delete Item"
              onClick={handleRemove}
            >
              <svg
                className="delete-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="22px"
                height="22px"
                viewBox="0 -960 960 960"
                fill="currentColor"
              >
                <path d="M312-144q-29.7 0-50.85-21.15Q240-186.3 240-216v-480h-48v-72h192v-48h192v48h192v72h-48v479.57Q720-186 698.85-165T648-144H312Zm336-552H312v480h336v-480ZM384-288h72v-336h-72v336Zm120 0h72v-336h-72v336ZM312-696v480-480Z"></path>
              </svg>
            </button>
          </div>
        </div>
      </li>
    </>
  );
}
